/**
 * /generations/manage — view the circles you're in, tune per-circle push,
 * and leave. Circle *administration* (create a circle, invite families) is
 * restricted to the app owner; those controls only render for them. The
 * browsing hub lives at /generations.
 *
 * Circle membership is grown by email only: the owner invites a household
 * by address, the recipient clicks a magic link that creates their family
 * and joins it to the circle. There's no shared join code.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useCircleInvitations, useMyCircles, type Circle } from '../lib/circles';
import { ScreenHeader } from '../components/ScreenHeader';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { IconButton } from '../components/IconButton';
import { Button } from '../components/Button';
import { IconArrowLeft, IconExit, IconPlus, IconXCircle } from '../components/icons';
import { TextField } from '../components/TextField';

export default function GenerationsManagePage() {
  const qc = useQueryClient();
  const auth = useAuth();
  const circles = useMyCircles();
  const [mode, setMode] = useState<'list' | 'create'>('list');
  const isOwner = auth.state === 'signed-in' && auth.me.isOwner;

  const create = useMutation({
    mutationFn: (args: { name: string; surfaces: Circle['surfaces'] }) =>
      api.post<{ circle: Circle }>('/circles', args),
    onSuccess: () => {
      setMode('list');
      void qc.invalidateQueries({ queryKey: ['circles'] });
    },
  });

  return (
    <>
      <ScreenHeader
        title="Manage circles"
        subtitle="Extended family — grandparents, aunts, cousins. Share selected things without dissolving what's just for our household."
        right={
          <Link
            to="/generations"
            aria-label="Back"
            title="Back"
            className="inline-flex items-center justify-center w-10 h-10 rounded-full text-soft hover:text-ink hover:bg-line/40 transition-colors"
          >
            <IconArrowLeft />
          </Link>
        }
      />
      <div className="px-5 pt-4 pb-6">
        {mode === 'list' && (
          <>
            {isOwner && (
              <div className="flex items-center gap-2 mb-4">
                <IconButton
                  icon={<IconPlus />}
                  label="New circle"
                  onClick={() => setMode('create')}
                />
              </div>
            )}

            {circles.data && circles.data.circles.length === 0 ? (
              <p className="text-muted italic text-center py-10">
                {isOwner
                  ? 'No circles yet. Start one with grandparents, then invite their household by email.'
                  : "You're not in any circles yet. When someone adds your family to one, it'll show up here."}
              </p>
            ) : (
              <div className="space-y-2">
                {circles.data?.circles.map((c) => (
                  <CircleCard key={c.id} circle={c} isOwner={isOwner} />
                ))}
              </div>
            )}
          </>
        )}

        {mode === 'create' && (
          <CreateForm
            busy={create.isPending}
            error={create.error instanceof ApiError ? create.error.message : undefined}
            onCancel={() => setMode('list')}
            onSubmit={(args) => create.mutate(args)}
          />
        )}
      </div>
    </>
  );
}

function CircleCard({ circle, isOwner }: { circle: Circle; isOwner: boolean }) {
  const qc = useQueryClient();
  // Inviting families is owner-only. (A member can still leave + mute.)
  const canAdminister = isOwner;
  const [inviteOpen, setInviteOpen] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);
  // One revoke dialog for the card, keyed by the invitation it targets.
  const [pendingRevoke, setPendingRevoke] = useState<{ id: string; who: string } | null>(
    null,
  );

  // Only the owner can read the invitations endpoint; gate the query so
  // members don't fire a request that 403s.
  const invitations = useCircleInvitations(canAdminister ? circle.id : null);

  const leave = useMutation({
    mutationFn: () => api.delete(`/circles/${circle.id}/leave`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['circles'] }),
  });
  const setMute = useMutation({
    mutationFn: (mutePushAt: number | null) =>
      api.patch(`/circles/${circle.id}/membership`, { mutePushAt }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['circles'] }),
  });
  const invite = useMutation({
    mutationFn: (args: { email: string; displayName?: string }) =>
      api.post(`/circles/${circle.id}/invite`, args),
    onSuccess: () => {
      setInviteOpen(false);
      void qc.invalidateQueries({ queryKey: ['circle-invitations', circle.id] });
    },
  });
  const revoke = useMutation({
    mutationFn: (invId: string) =>
      api.delete(`/circles/${circle.id}/invitations/${invId}`),
    onSuccess: () => {
      setPendingRevoke(null);
      void qc.invalidateQueries({ queryKey: ['circle-invitations', circle.id] });
    },
  });

  const pending = invitations.data?.invitations ?? [];

  return (
    <article className="card p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <h2 className="font-serif text-xl text-ink">{circle.name}</h2>
          <div className="kicker text-soft mt-1">
            {circle.role === 'admin' ? 'Admin' : 'Member'} ·{' '}
            {surfaceLabel(circle.surfaces)}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          <label className="flex items-center gap-2 text-xs text-soft cursor-pointer">
            <input
              type="checkbox"
              checked={!!circle.mutePushAt && circle.mutePushAt > Date.now()}
              onChange={(e) =>
                setMute.mutate(
                  // "Forever" = 100 years out; clearing resets to null.
                  // Either way the daily-push + mention fanout checks
                  // `mute_push_at > now`, so the semantics stay simple.
                  e.target.checked ? Date.now() + 100 * 365 * 86_400_000 : null,
                )
              }
              className="accent-oxblood"
            />
            Mute push
          </label>
          {circle.role !== 'admin' && (
            <IconButton
              icon={<IconExit />}
              label={`Leave ${circle.name}`}
              variant="destructive"
              busy={leave.isPending}
              onClick={() => setLeaveOpen(true)}
            />
          )}
        </div>
      </div>

      {leaveOpen && (
        <ConfirmDialog
          title={`Leave ${circle.name}?`}
          message="You'll stop seeing what's shared with this circle. You can be re-invited later."
          confirmLabel="Leave"
          destructive
          busy={leave.isPending}
          error={leave.isError ? 'Could not leave. Try again.' : null}
          onConfirm={() => leave.mutate()}
          onCancel={() => setLeaveOpen(false)}
        />
      )}

      {canAdminister && (
        <div className="mt-4 border-t border-line pt-3">
          {!inviteOpen ? (
            <button
              onClick={() => setInviteOpen(true)}
              className="h-9 px-3 rounded-lg border-2 border-oxblood text-oxblood text-sm font-medium"
            >
              Invite a family
            </button>
          ) : (
            <InviteForm
              busy={invite.isPending}
              error={invite.error instanceof ApiError ? invite.error.message : undefined}
              onCancel={() => setInviteOpen(false)}
              onSubmit={(args) => invite.mutate(args)}
            />
          )}

          {pending.length > 0 && (
            <div className="mt-3 space-y-1.5">
              <div className="kicker text-soft">Pending invitations</div>
              {pending.map((inv) => (
                <div
                  key={inv.id}
                  className="flex items-center justify-between gap-2 text-sm"
                >
                  <span className="text-ink truncate">
                    {inv.displayName ? `${inv.displayName} · ` : ''}
                    <span className="text-soft">{inv.email}</span>
                  </span>
                  <IconButton
                    icon={<IconXCircle />}
                    label={`Revoke invitation for ${inv.displayName || inv.email}`}
                    variant="destructive"
                    busy={revoke.isPending && pendingRevoke?.id === inv.id}
                    onClick={() =>
                      setPendingRevoke({ id: inv.id, who: inv.displayName || inv.email })
                    }
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {pendingRevoke && (
        <ConfirmDialog
          title={`Revoke ${pendingRevoke.who}'s invitation?`}
          message="The link in their inbox will stop working."
          confirmLabel="Revoke"
          destructive
          busy={revoke.isPending}
          error={revoke.isError ? 'Could not revoke. Try again.' : null}
          onConfirm={() => revoke.mutate(pendingRevoke.id)}
          onCancel={() => setPendingRevoke(null)}
        />
      )}
    </article>
  );
}

function surfaceLabel(s: Circle['surfaces']): string {
  const on = [s.photos && 'Photos', s.prayer && 'Prayer', s.chat && 'Chat'].filter(
    Boolean,
  );
  return on.join(' · ') || 'No surfaces';
}

function CreateForm({
  busy,
  error,
  onCancel,
  onSubmit,
}: {
  busy: boolean;
  error?: string;
  onCancel: () => void;
  onSubmit: (args: { name: string; surfaces: Circle['surfaces'] }) => void;
}) {
  const [name, setName] = useState('');
  const [photos, setPhotos] = useState(true);
  const [prayer, setPrayer] = useState(true);
  const [chat, setChat] = useState(false);
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!name.trim()) return;
        onSubmit({ name: name.trim(), surfaces: { photos, prayer, chat } });
      }}
      className="card p-5 space-y-4"
    >
      <div className="-ml-2">
        <IconButton icon={<IconArrowLeft />} label="Back" onClick={onCancel} />
      </div>
      <label className="block">
        <span className="block text-sm text-soft mb-1">Circle name</span>
        <TextField
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Crocker Extended"
          autoFocus
        />
      </label>
      <div>
        <span className="block text-sm text-soft mb-2">Share with this circle</span>
        <div className="space-y-2">
          <Toggle label="Photos" value={photos} onChange={setPhotos} />
          <Toggle label="Prayer requests" value={prayer} onChange={setPrayer} />
          <Toggle label="Chat messages" value={chat} onChange={setChat} />
        </div>
      </div>
      {error && <p className="text-oxblood text-sm">{error}</p>}
      <Button type="submit" block size="lg" disabled={!name.trim()} busy={busy}>
        Create circle
      </Button>
    </form>
  );
}

function InviteForm({
  busy,
  error,
  onCancel,
  onSubmit,
}: {
  busy: boolean;
  error?: string;
  onCancel: () => void;
  onSubmit: (args: { email: string; displayName?: string }) => void;
}) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!email.trim()) return;
        onSubmit({ email: email.trim(), displayName: name.trim() || undefined });
      }}
      className="space-y-3"
    >
      <label className="block">
        <span className="block text-sm text-soft mb-1">Their email</span>
        <TextField
          type="email"
          inputMode="email"
          autoComplete="off"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="grandma@example.com"
          autoFocus
        />
      </label>
      <label className="block">
        <span className="block text-sm text-soft mb-1">Their name (optional)</span>
        <TextField
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Grandma"
          maxLength={40}
        />
      </label>
      <p className="text-xs text-muted">
        They'll get a link to set up their own household and join this circle.
      </p>
      {error && <p className="text-oxblood text-sm">{error}</p>}
      <div className="flex gap-2">
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={!email.trim()} busy={busy}>
          Send invite
        </Button>
      </div>
    </form>
  );
}

function Toggle({
  label,
  value,
  onChange,
}: {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center justify-between py-2">
      <span className="text-ink">{label}</span>
      <input
        type="checkbox"
        checked={value}
        onChange={(e) => onChange(e.target.checked)}
        className="accent-oxblood w-5 h-5"
      />
    </label>
  );
}
